import { z } from "zod";

import {
  boundedTextSchema,
  hasUniqueValues,
  kebabCaseIdentifierSchema
} from "@/domain/schemas";

const MIN_ITEMS = 2;
const MAX_ITEMS = 12;

export const choiceMatchItemSchema = z.strictObject({
  id: kebabCaseIdentifierSchema,
  label: boundedTextSchema(120)
});

const choiceMatchItemsSchema = z
  .array(choiceMatchItemSchema)
  .min(MIN_ITEMS)
  .max(MAX_ITEMS)
  .refine((items) => hasUniqueValues(items.map(({ id }) => id)), {
    message: "Item identifiers must be unique"
  });

export const singleChoicePayloadSchema = z
  .strictObject({
    kind: z.literal("single-choice"),
    choices: choiceMatchItemsSchema,
    correctChoiceId: kebabCaseIdentifierSchema
  })
  .superRefine((payload, context) => {
    if (!payload.choices.some(({ id }) => id === payload.correctChoiceId)) {
      context.addIssue({
        code: "custom",
        path: ["correctChoiceId"],
        message: "Correct choice must reference a listed choice"
      });
    }
  });

const correctChoiceIdsSchema = z
  .array(kebabCaseIdentifierSchema)
  .min(1)
  .max(MAX_ITEMS)
  .refine((ids) => hasUniqueValues(ids), {
    message: "Correct choice identifiers must be unique"
  });

export const multipleChoicePayloadSchema = z
  .strictObject({
    kind: z.literal("multiple-choice"),
    choices: choiceMatchItemsSchema,
    correctChoiceIds: correctChoiceIdsSchema
  })
  .superRefine((payload, context) => {
    const choiceIds = new Set(payload.choices.map(({ id }) => id));

    for (const [index, correctChoiceId] of payload.correctChoiceIds.entries()) {
      if (!choiceIds.has(correctChoiceId)) {
        context.addIssue({
          code: "custom",
          path: ["correctChoiceIds", index],
          message: "Correct choice must reference a listed choice"
        });
      }
    }
  });

export const matchingPairSchema = z.strictObject({
  leftId: kebabCaseIdentifierSchema,
  rightId: kebabCaseIdentifierSchema
});

export const matchingPayloadSchema = z
  .strictObject({
    kind: z.literal("matching"),
    leftItems: choiceMatchItemsSchema,
    rightItems: choiceMatchItemsSchema,
    pairs: z.array(matchingPairSchema).min(MIN_ITEMS).max(MAX_ITEMS)
  })
  .superRefine((payload, context) => {
    const leftIds = new Set(payload.leftItems.map(({ id }) => id));
    const rightIds = new Set(payload.rightItems.map(({ id }) => id));
    const pairedLeftIds = payload.pairs.map(({ leftId }) => leftId);
    const pairedRightIds = payload.pairs.map(({ rightId }) => rightId);

    if (
      payload.leftItems.length !== payload.rightItems.length ||
      payload.pairs.length !== payload.leftItems.length
    ) {
      context.addIssue({
        code: "custom",
        path: ["pairs"],
        message: "Matching must pair every left and right item exactly once"
      });
    }

    if (
      !hasUniqueValues(pairedLeftIds) ||
      !hasUniqueValues(pairedRightIds)
    ) {
      context.addIssue({
        code: "custom",
        path: ["pairs"],
        message: "Matching pairs must be one-to-one"
      });
    }

    for (const [index, pair] of payload.pairs.entries()) {
      if (!leftIds.has(pair.leftId)) {
        context.addIssue({
          code: "custom",
          path: ["pairs", index, "leftId"],
          message: "Pair must reference a listed left item"
        });
      }

      if (!rightIds.has(pair.rightId)) {
        context.addIssue({
          code: "custom",
          path: ["pairs", index, "rightId"],
          message: "Pair must reference a listed right item"
        });
      }
    }

    if (
      payload.leftItems.some(({ id }) => !pairedLeftIds.includes(id)) ||
      payload.rightItems.some(({ id }) => !pairedRightIds.includes(id))
    ) {
      context.addIssue({
        code: "custom",
        path: ["pairs"],
        message: "Matching pairs must cover every item"
      });
    }
  });

export const choiceMatchPayloadSchema = z.discriminatedUnion("kind", [
  singleChoicePayloadSchema,
  multipleChoicePayloadSchema,
  matchingPayloadSchema
]);

export type ChoiceMatchItem = z.infer<typeof choiceMatchItemSchema>;
export type SingleChoicePayload = z.infer<typeof singleChoicePayloadSchema>;
export type MultipleChoicePayload = z.infer<typeof multipleChoicePayloadSchema>;
export type ChoicePayload = SingleChoicePayload | MultipleChoicePayload;
export type MatchingPair = z.infer<typeof matchingPairSchema>;
export type MatchingPayload = z.infer<typeof matchingPayloadSchema>;
export type ChoiceMatchPayload = z.infer<typeof choiceMatchPayloadSchema>;
